import { DEFAULT_CERTIFICATE_FULFILLMENT_MODES_ALLOWED } from "@/constants/certificateFulfillmentModes";
import type { CertificateProductFormValues } from "@/pages/platform/certificate-product-form-types";
import { minorToPesoInput } from "@/pages/platform/certificate-product-form-submit";
import type { CertificateProductPriceRow } from "@/services/platformCertificatePricingService";

export function defaultChannelsForTargetType(targetType: string): {
  involvesAccountant: boolean;
  involvesNotary: boolean;
} {
  if (targetType === "income_certificate") {
    return { involvesAccountant: true, involvesNotary: false };
  }
  return { involvesAccountant: false, involvesNotary: true };
}

export function emptyCertificateProductForm(): CertificateProductFormValues {
  return {
    targetType: "",
    displayName: "",
    fixedCostItems: [{ label: "", pesos: minorToPesoInput(0), includesIva: true }],
    urUnits: "0",
    urType: "dgi",
    marginPercent: "0",
    perFileStoragePesos: minorToPesoInput(0),
    deliveryFeePesos: minorToPesoInput(0),
    professionalStampPesos: minorToPesoInput(0),
    consumerServiceFeePesos: minorToPesoInput(0),
    urVariableIncludesIva: true,
    perFileStorageIncludesIva: true,
    deliveryFeeIncludesIva: true,
    marginIncludesIva: false,
    sortOrder: "0",
    currency: "UYU",
    involvesAccountant: false,
    involvesNotary: true,
    requiredDocs: [],
    fulfillmentModes: [...DEFAULT_CERTIFICATE_FULFILLMENT_MODES_ALLOWED],
    active: true,
    defaultBusinessDays: "0",
    description: "",
    descriptionPreviewOpen: false,
  };
}

export function certificateProductFormFromRow(row: CertificateProductPriceRow): CertificateProductFormValues {
  const fixedCostItems =
    Array.isArray(row.fixedCostItems) && row.fixedCostItems.length > 0
      ? row.fixedCostItems.map((item) => ({
          label: item.label,
          pesos: minorToPesoInput(item.amountMinor),
          includesIva: item.includesIva,
        }))
      : [
          {
            label: "",
            pesos: minorToPesoInput(row.fixedAmountMinor),
            includesIva: row.fixedAmountIncludesIva,
          },
        ];

  return {
    targetType: row.targetType,
    displayName: row.displayName,
    fixedCostItems,
    urUnits: String(parseFloat(row.urUnits) || 0),
    urType: row.urType === "arancel" ? "arancel" : "dgi",
    marginPercent: String(parseFloat(row.marginPercent) || 0),
    perFileStoragePesos: minorToPesoInput(row.perFileStorageMinor ?? 0),
    deliveryFeePesos: minorToPesoInput(row.deliveryFeeMinor ?? 0),
    professionalStampPesos: minorToPesoInput(row.professionalStampMinor ?? 0),
    consumerServiceFeePesos: minorToPesoInput(row.consumerServiceFeeMinor ?? 0),
    sortOrder: String(row.sortOrder ?? 0),
    currency: row.currency?.trim() || "UYU",
    involvesAccountant: row.involvesAccountant,
    involvesNotary: row.involvesNotary,
    requiredDocs: row.requiredDocumentTypes ?? [],
    fulfillmentModes:
      row.fulfillmentModesAllowed != null && row.fulfillmentModesAllowed.length > 0
        ? row.fulfillmentModesAllowed
        : [...DEFAULT_CERTIFICATE_FULFILLMENT_MODES_ALLOWED],
    urVariableIncludesIva: row.urVariableIncludesIva,
    perFileStorageIncludesIva: row.perFileStorageIncludesIva,
    deliveryFeeIncludesIva: row.deliveryFeeIncludesIva,
    marginIncludesIva: row.marginIncludesIva ?? false,
    active: row.isActive,
    defaultBusinessDays: String(row.defaultBusinessDays ?? 0),
    description: row.description ?? "",
    descriptionPreviewOpen: false,
  };
}
